/**
 * Which portal ACCOUNT belongs to an owner (lessor) record.
 *
 * The TypeScript mirror of `lessor_for_owner_user()` read in reverse: an
 * account is linked either explicitly (property_owners.lessor_id) or, failing
 * that, by matching email. Kept in one place because the Owners list and the
 * owner profile both need the answer, and two copies of a matching rule that
 * decides "is this the same person" is how they end up disagreeing.
 *
 * Display only. Anything WRITING across the link goes through the database
 * resolver instead, which additionally refuses accounts that map to several
 * owner records — see the migration
 * 20260816140000_account_profile_syncs_to_owner_record.sql.
 */
import type { AdminProfileDirectoryRow } from "@/lib/fetch-admin-profiles-directory";

/** Just enough of an owner record to resolve its account. */
type OwnerLike = { id: string; email?: string | null };

export function findOwnerAccount(
  owner: OwnerLike | null | undefined,
  accounts: AdminProfileDirectoryRow[] | null | undefined
): AdminProfileDirectoryRow | null {
  if (!owner || !accounts) return null;
  const byLessor = accounts.find((a) =>
    a.property_owners.some((po) => po.lessor_id === owner.id)
  );
  if (byLessor) return byLessor;
  const email = owner.email?.trim().toLowerCase();
  if (!email) return null;
  return accounts.find((a) => a.email?.trim().toLowerCase() === email) ?? null;
}

/**
 * The account's name when it disagrees with the owner record's, else null.
 *
 * Only meaningful for `Individual` records: for a Company or GbR the record
 * names the ORGANISATION and the account names its representative, so the two
 * are *supposed* to differ and flagging it would cry wolf on every company.
 */
export function ownerAccountNameDrift(params: {
  ownerName: string | null | undefined;
  ownerType: string | null | undefined;
  accountName: string | null | undefined;
}): string | null {
  const { ownerName, ownerType, accountName } = params;
  if (ownerType !== "Individual") return null;
  const account = accountName?.trim();
  if (!account) return null;
  if (account.toLowerCase() === (ownerName ?? "").trim().toLowerCase())
    return null;
  return account;
}

/** A property an ACCOUNT is assigned to, resolved to something displayable. */
export type AccountProperty = {
  id: number;
  title: string | null;
  city: string | null;
  /**
   * Property-level onboarding isn't derivable without a billing entity, so it
   * is reported as `pending` and the surfaces that show these rows carry the
   * account's real portal state next to them instead.
   */
  onboardingStage: "pending";
};

/**
 * The properties an owner who has NO billing entity is assigned to.
 *
 * Their properties come from `property_owners`, which stores ids only — the
 * titles are resolved against the property list the admin surfaces already load
 * for their pickers. Without that lookup a row would show bare numbers, so an
 * unresolved id is dropped rather than rendered as "Property 7".
 *
 * Shared by the Owners table row and the account-only owner profile so the two
 * can't disagree about which buildings an owner is attached to.
 */
export function accountPropertyList(
  account: AdminProfileDirectoryRow | null | undefined,
  propertiesById?: Map<number, { title: string | null; city: string | null }>
): AccountProperty[] {
  if (!account || !propertiesById) return [];
  const seen = new Set<number>();
  const out: AccountProperty[] = [];
  for (const po of account.property_owners ?? []) {
    const id = Number(po.property_id);
    if (seen.has(id)) continue;
    const found = propertiesById.get(id);
    if (!found) continue;
    seen.add(id);
    out.push({
      id,
      title: found.title,
      city: found.city,
      onboardingStage: "pending",
    });
  }
  return out;
}
